const ENDPOINT = "/frontend-errors";
const WINDOW_KEY = "__trackerFrontendErrorReporting";
const WINDOW_MS = 60_000;
const MAX_REPORTS = 10;

function state() {
    if (!window[WINDOW_KEY]) {
        window[WINDOW_KEY] = {
            source: "inertia",
            installed: false,
            recent: new Map(),
            sent: [],
            pausedUntil: 0,
        };
    }
    return window[WINDOW_KEY];
}

// Only error text is collected. Request bodies, headers and arbitrary objects
// must never be serialized into a browser report.
function sanitize(value, limit) {
    if (typeof value !== "string") return "";
    return value
        .slice(0, 16_000)
        .replace(/https?:\/\/[^\s<>"')]+/gi, (url) => {
            try {
                const parsed = new URL(url);
                return `${parsed.origin}${parsed.pathname}`;
            } catch {
                return "[url]";
            }
        })
        .replace(
            /\/[^\s"'<>?#)]*[?#][^\s"'<>)]*/g,
            (url) => url.split(/[?#]/)[0],
        )
        .replace(/([?&][\w.-]+)=([^\s&#"']*)/g, "$1=[redacted]")
        .replace(
            /(\/(?:password-reset|file-drop)\/)[^/?#\s"'<>)]*/gi,
            "$1[redacted]",
        )
        .replace(/\b(Bearer|Basic)\s+[^\s,"'}]+/gi, "$1 [redacted]")
        .replace(/\beyJ[\w-]+\.[\w-]+\.[\w-]+\b/g, "[redacted]")
        .replace(
            /(["']?(?:password|passwd|secret|(?:access[_-]?|refresh[_-]?|csrf[_-]?|api[_-]?)?token|api[_-]?key|authorization|cookie|credentials?|session(?:[_-]?id)?)["']?\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^\s,;}]+)/gi,
            "$1[redacted]",
        )
        .replace(/[\w.+-]+@[\w.-]+\.[a-z]{2,}/gi, "[email]")
        .replace(/[\p{Cc}\p{Cf}]/gu, (character) =>
            ["\n", "\r", "\t"].includes(character) ? character : "",
        )
        .slice(0, limit);
}

function isExpected(error) {
    if (!error || typeof error !== "object") return false;
    return (
        error.name === "AbortError" ||
        error.name === "CanceledError" ||
        error.code === "ERR_CANCELED" ||
        error.message === "Request aborted" ||
        error.__CANCEL__ === true ||
        [401, 403, 419, 422].includes(error.response?.status)
    );
}

function csrfHeaders() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    if (token) return { "X-CSRF-TOKEN": token };
    const cookie = document.cookie
        .split("; ")
        .find((entry) => entry.startsWith("XSRF-TOKEN="));
    return cookie
        ? { "X-XSRF-TOKEN": decodeURIComponent(cookie.slice(11)) }
        : null;
}

/**
 * @param {unknown} error
 * @param {{source?: "legacy" | "inertia", kind?: "runtime" | "unhandledrejection" | "react" | "handled" | "inertia", componentStack?: string | null}} options
 */
export function reportFrontendError(error, options = {}) {
    try {
        if (typeof window === "undefined" || isExpected(error)) return;
        const reporter = state();
        const now = Date.now();
        if (now < reporter.pausedUntil || typeof window.fetch !== "function")
            return;
        const csrf = csrfHeaders();
        if (!csrf) return;

        const object = error && typeof error === "object" ? error : {};
        const message =
            sanitize(
                typeof error === "string" ? error : object.message,
                2000,
            ) || "Unexpected browser error";
        const payload = {
            source: options.source || reporter.source,
            kind: options.kind || "handled",
            name: sanitize(object.name || "Error", 100),
            message,
            stack: sanitize(object.stack, 8000),
            component_stack: sanitize(options.componentStack, 4000),
            page: sanitize(window.location.pathname, 512),
            version:
                typeof __APP_VERSION__ === "string"
                    ? __APP_VERSION__.slice(0, 100)
                    : "",
        };
        // Exclude kind/component stack: a React failure can also fire a window
        // event, and the same caught error can be rethrown by its caller.
        const fingerprint = JSON.stringify([
            payload.name,
            message,
            payload.stack,
            payload.page,
        ]);
        for (const [key, timestamp] of reporter.recent) {
            if (now - timestamp >= WINDOW_MS) reporter.recent.delete(key);
        }
        reporter.sent = reporter.sent.filter(
            (timestamp) => now - timestamp < WINDOW_MS,
        );
        if (
            reporter.recent.has(fingerprint) ||
            reporter.sent.length >= MAX_REPORTS
        )
            return;
        reporter.recent.set(fingerprint, now);
        reporter.sent.push(now);

        let body = JSON.stringify(payload);
        if (new Blob([body]).size > 32_768) {
            payload.stack = payload.stack.slice(0, 3000);
            payload.component_stack = payload.component_stack.slice(0, 1000);
            body = JSON.stringify(payload);
        }

        // A separate fetch transport prevents Axios error handlers from trying
        // to report the failure of this report. Never retry or throw here.
        void window
            .fetch(ENDPOINT, {
                method: "POST",
                credentials: "same-origin",
                redirect: "error",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    ...csrf,
                },
                body,
            })
            .then((response) => {
                if ([401, 403, 419, 429].includes(response.status)) {
                    reporter.pausedUntil = Date.now() + WINDOW_MS;
                }
            })
            .catch(() => {});
    } catch {
        // Reporting must not change the application's error handling.
    }
}

/** @param {"legacy" | "inertia"} source */
export function installFrontendErrorReporting(source) {
    if (typeof window === "undefined") return;
    const reporter = state();
    reporter.source = source;
    if (reporter.installed) return;
    reporter.installed = true;
    window.addEventListener("error", (event) => {
        if (!(event instanceof ErrorEvent)) return;
        reportFrontendError(
            event.error || {
                name: "Error",
                message: event.message,
                stack: event.filename
                    ? `${event.filename}:${event.lineno}:${event.colno}`
                    : "",
            },
            { kind: "runtime" },
        );
    });
    window.addEventListener("unhandledrejection", (event) => {
        reportFrontendError(event.reason, { kind: "unhandledrejection" });
    });
}
